import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScreenData } from "../../types/ScreenTypes";
import { fetchEventsICS, hydrateEvents } from "../../services/eventsService";
import { mixWithWhite, readableColour } from "../../utils/colourContrast";
import useLabbaikItems, { labbaikEvents } from "../../hooks/useLabbaikItems";
import "./SimpleUpcomingEventsWidget.css";

interface SimpleUpcomingEventsWidgetProps {
  masjidId: number;
  duration: number;
  onComplete: () => void;
  screenData: ScreenData;
  // Custom colors for theming (uses same 4-color system as salah changes)
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  widgetBackgroundColor?: string;
  // Container dimensions for responsive behavior
  width?: number;
  height?: number;
  // Progress tracking props
  currentIndex?: number;
  totalItems?: number;
  elapsedTime?: number;
  totalDuration?: number;
  // Restart trigger to reset the component without unmounting
  restartTrigger?: number;
  // Behavior controls
  hideWhenNoEvents?: boolean;
  onNoEvents?: () => void;
  // Shown instead of the masjid's events, without fetching (colour preview)
  sampleEvents?: ParsedEvent[];
}

interface ParsedEvent {
  uid: string;
  summary: string;
  description?: string;
  location?: string;
  speakers?: string;
  audience?: string;
  start: Date;
  end?: Date;
  allDay?: boolean;
  timeLabel?: string;
}

interface GroupedEvents {
  date: string;
  displayDate: string;
  dayName: string;
  isToday: boolean;
  isTomorrow: boolean;
  events: ParsedEvent[];
}

const SimpleUpcomingEventsWidget: React.FC<SimpleUpcomingEventsWidgetProps> = ({
  masjidId,
  duration,
  onComplete,
  screenData,
  primaryColor = "#F5A623", // Warm Amber/Gold
  secondaryColor = "#FF8C42", // Soft Orange
  accentColor = "#10B981", // Emerald Green
  widgetBackgroundColor = "#0F172A", // Rich Dark (Slate 900)
  width = 300,
  height = 400,
  currentIndex,
  totalItems,
  elapsedTime,
  totalDuration,
  restartTrigger = 0,
  hideWhenNoEvents = false,
  onNoEvents,
  sampleEvents,
}) => {
  // Timer management
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef<boolean>(false);
  const onCompleteCallbackRef = useRef(onComplete);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false);
  const noEventsHandledRef = useRef(false);

  // State
  const [events, setEvents] = useState<ParsedEvent[]>(sampleEvents ?? []);
  const [loading, setLoading] = useState(!sampleEvents);
  const [error, setError] = useState<string | null>(null);
  const labbaikItems = useLabbaikItems(masjidId, screenData.showLabbaikEvents ?? screenData.showLabbaik);
  const allEvents = useMemo(
    () => [...events, ...labbaikEvents(labbaikItems, new Date())],
    [events, labbaikItems],
  );

  // Update the callback ref when onComplete changes
  useEffect(() => {
    onCompleteCallbackRef.current = onComplete;
  }, [onComplete]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset completion flag when restartTrigger changes (but NOT refetch)
  useEffect(() => {
    hasCompletedRef.current = false;
    noEventsHandledRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [restartTrigger]);

  // Set up the auto-advance timer - separate from data fetching
  useEffect(() => {
    // Don't start timer if still loading or already completed
    if (loading || hasCompletedRef.current) {
      return;
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set up new timer for duration
    timerRef.current = setTimeout(() => {
      if (!hasCompletedRef.current && isMountedRef.current) {
        hasCompletedRef.current = true;
        onCompleteCallbackRef.current?.();
      }
    }, duration * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [duration, loading, restartTrigger]);

  // Fetch events and listen for updates
  useEffect(() => {
    if (sampleEvents) return;

    async function loadEvents() {
      if (!isMountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        // Use timesRefreshInterval from screenData (default 300 seconds / 5 minutes)
        const refreshInterval = screenData.timesRefreshInterval || 300;
        const storedEvents = await fetchEventsICS(masjidId, refreshInterval);

        if (!isMountedRef.current) return;

        // Hydrate stored events (convert ISO strings back to Date objects)
        const hydratedEvents = hydrateEvents(storedEvents);
        setEvents(hydratedEvents);
      } catch (err) {
        if (!isMountedRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to load events");
        setEvents([]);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }

    const handleEventsUpdated = () => {
      loadEvents();
    };

    if (masjidId) {
      loadEvents(); // Initial load
    } else {
      setLoading(false);
    }

    window.addEventListener("events-updated", handleEventsUpdated);
    return () =>
      window.removeEventListener("events-updated", handleEventsUpdated);
  }, [masjidId, screenData.timesRefreshInterval, sampleEvents]);

  // Group events by day for the next 7 days
  const groupedEvents = useMemo<GroupedEvents[]>(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const groups: GroupedEvents[] = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(today);
      day.setDate(today.getDate() + i);
      const dayEnd = new Date(day);
      dayEnd.setDate(day.getDate() + 1);

      const dayKey = day.toISOString().split("T")[0];

      // Filter events for this day
      const dayEvents = allEvents.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= day && eventDate < dayEnd;
      });

      // For today, only show events that haven't finished yet (future or ongoing)
      const filteredEvents =
        i === 0
          ? dayEvents.filter((event) => {
              if (event.allDay) return true;
              // Show if event hasn't started yet OR is currently ongoing
              return event.start >= now || (event.end && event.end > now);
            })
          : dayEvents;

      groups.push({
        date: dayKey,
        displayDate: day.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        dayName: day.toLocaleDateString(undefined, { weekday: "long" }),
        isToday: i === 0,
        isTomorrow: i === 1,
        events: filteredEvents,
      });
    }

    return groups;
  }, [allEvents]);

  // Calculate how many days to show based on available height
  const visibleGroups = useMemo(() => {
    // Filter out days with no events (optional: keep all days to show "No events")
    const groupsWithEvents = groupedEvents.filter((g) => g.events.length > 0);

    if (groupsWithEvents.length === 0) {
      return groupedEvents.slice(0, 3); // Show first 3 days even if empty
    }

    // Estimate space needed per group
    const headerHeight = 60; // px for widget header
    const dayHeaderHeight = 36; // px per day header
    const eventItemHeight = 50; // px per event item
    const padding = 24; // total padding

    let usedHeight = headerHeight + padding;
    const visible: GroupedEvents[] = [];

    for (const group of groupedEvents) {
      const groupHeight =
        dayHeaderHeight + Math.max(1, group.events.length) * eventItemHeight;

      if (usedHeight + groupHeight <= height) {
        visible.push(group);
        usedHeight += groupHeight;
      } else if (visible.length === 0) {
        // Always show at least one day
        visible.push(group);
        break;
      } else {
        break;
      }
    }

    return visible;
  }, [groupedEvents, height]);

  // Total events count for display
  const totalEventsCount = useMemo(() => {
    return groupedEvents.reduce((sum, g) => sum + g.events.length, 0);
  }, [groupedEvents]);

  useEffect(() => {
    if (
      hideWhenNoEvents &&
      !loading &&
      totalEventsCount === 0 &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      if (!noEventsHandledRef.current) {
        noEventsHandledRef.current = true;
        onNoEvents?.();
      }
      onCompleteCallbackRef.current?.();
    }
  }, [hideWhenNoEvents, loading, totalEventsCount, onNoEvents]);

  // CSS custom properties for theming - 4-color system
  const resolvedPrimary = screenData.primaryColor || primaryColor;
  const resolvedSecondary = screenData.secondaryColor || secondaryColor;
  const resolvedAccent = screenData.accentColor || accentColor;
  const resolvedBackground = screenData.widgetBackgroundColor || widgetBackgroundColor;
  // Event times sit on the cards, so they must read against the card colour
  const cardColour = mixWithWhite(resolvedBackground);
  const customStyles = {
    "--events-primary-color": resolvedPrimary,
    "--events-secondary-color": resolvedSecondary,
    "--events-accent-color": resolvedAccent,
    "--events-background-color": resolvedBackground,
    "--events-time-color": readableColour([resolvedAccent, resolvedSecondary, resolvedPrimary], cardColour),
    "--events-soon-color": readableColour([resolvedSecondary, resolvedPrimary, resolvedAccent], cardColour),
    "--heading-text-color": screenData.headingTextColor || "#FFFFFF",
    width: "100%",
    height: "100%",
  } as React.CSSProperties;

  // Format time for display
  const formatTime = (date: Date, allDay?: boolean): string => {
    if (allDay) return "All Day";

    const use12Hour = screenData.use12HourFormat !== false;
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: use12Hour,
    });
  };

  if (loading) {
    return (
      <div className="simple-upcoming-events-widget" style={customStyles}>
        <div className="events-widget-header">
          <h3>Upcoming Events</h3>
        </div>
        <div className="events-widget-content">
          <div className="events-loading">
            <div className="events-loading-spinner"></div>
            <p>Loading events...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hideWhenNoEvents && totalEventsCount === 0) {
    return null;
  }

  return (
    <div className="simple-upcoming-events-widget" style={customStyles}>
      <div className="events-widget-header">
        <h3>Upcoming Events</h3>
        {totalEventsCount > 0 && (
          <span className="events-count">
            {totalEventsCount} event{totalEventsCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="events-widget-content">
        {totalEventsCount === 0 ? (
          <div className="events-no-events">
            <p>No upcoming events</p>
            <span>Check back soon!</span>
          </div>
        ) : (
          <div className="events-list">
            {visibleGroups
              .filter((group) => group.events.length > 0) // Only show days with events
              .map((group) => (
                <div key={group.date} className="events-day-group">
                  <div
                    className={`events-day-header ${group.isToday ? "today" : ""} ${group.isTomorrow ? "tomorrow" : ""}`}
                  >
                    <span className="events-day-name">
                      {group.isToday
                        ? "Today"
                        : group.isTomorrow
                          ? "Tomorrow"
                          : group.dayName}
                    </span>
                    <span className="events-day-date">{group.displayDate}</span>
                  </div>

                  <div className="events-day-items">
                    {group.events
                      .slice() // Create copy to avoid mutating original
                      .sort((a, b) => {
                        // All-day events first (like iOS)
                        if (a.allDay && !b.allDay) return -1;
                        if (!a.allDay && b.allDay) return 1;
                        // Then by start time
                        return a.start.getTime() - b.start.getTime();
                      })
                      .map((event, idx) => {
                        const now = new Date();
                        const isOngoing =
                          !event.allDay &&
                          event.start <= now &&
                          event.end &&
                          event.end > now;
                        const isUpcoming =
                          !event.allDay &&
                          event.start > now &&
                          event.start.getTime() - now.getTime() <=
                            30 * 60 * 1000; // Within 30 min

                        return (
                          <div
                            key={`${event.uid}-${idx}`}
                            className={`events-item ${isOngoing ? "ongoing" : ""} ${isUpcoming ? "upcoming-soon" : ""}`}
                          >
                            <div className="events-item-accent"></div>
                            <div className="events-item-time">
                              {isOngoing
                                ? "Now"
                                : event.timeLabel || formatTime(event.start, event.allDay)}
                            </div>
                            <div className="events-item-details">
                              <div className="events-item-title">
                                {event.summary}
                              </div>
                              {event.audience && (
                                <div className="events-item-audience">
                                  👥 {event.audience}
                                </div>
                              )}
                              {event.location && (
                                <div className="events-item-location">
                                  📍 {event.location}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleUpcomingEventsWidget;
